import { useState } from 'react'
import {
  Box,
  IconButton,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreHoriz as MoreHorizIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  FILTER_ROW_SX,
  ICON_TEXT_GAP_PX,
} from '@/apps/latest/components/organisms/Sidebar/ContextPane'
import { SidebarShell } from '@/apps/latest/components/organisms/Sidebar/SidebarShell'
import {
  RECORDING_NAV_BLOCK_HINT,
  useActiveRecording,
} from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_MENU_PAPER_SX } from '@/apps/latest/shared/styles/menuStyles'
import { LAYOUT } from '@/shared/styles/spacing'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'
import { RADIUS } from '@/shared/styles/radii'

import type { NutritionCustomer } from './api'
import { useNutritionBrand } from './brand'
import { formatMd } from './format'
import { NutritionCustomerDialog } from './NutritionCustomerDialog'
import { useNutritionCustomerSearch } from './useNutritionCustomers'

interface Props {
  onToggle?: () => void
  // route 遷移時に呼ぶ callback (スマホ Drawer のクローズ用)。
  onNavigate?: () => void
}

// 登録の面へ渡す絞りの字 (pages/nutrition/new が受けて、字の形で入れる欄を分ける)
export interface NewCustomerState {
  text?: string
}

// 管理栄養士の左ペイン = お客様の司令塔 (design-book ADR-0086・04-apps/kakeru.md「管理栄養士の記録」)。
// 上から 絞りの欄 (HomePane のフィルタ行と同じ枠) + 「＋ 新しいお客様を登録」の行 + お客様の行。
// お客様の行は 氏名 + 右端に最後に記録を始めた日 (back の並び。着地していない今回も数え、中身の無い器は数えない)。
// 行の ⋯ (hover-actions) に「お客様の情報」(dialog。直す場所はここ 1 つ)。日付の帯・記録の列・polling は持たない
// (管理栄養士は流れ込みが無く、店で 1 アカウント)。録音中に塞ぐのは面を移る手 (ほかのお客様の行・＋ の行) だけで、
// 絞りの欄と ⋯ の dialog は面を離れないので使える。
// 見出しは `Kakeru 管理栄養士版` (マイライフの店舗では `オールラボ版`。brand.ts)。
export const NutritionSidebar = ({ onToggle, onNavigate }: Props) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { customerId: openCustomerId } = useParams<{ customerId: string }>()
  const { isActiveRecording } = useActiveRecording()
  const [query, setQuery] = useState('')
  const { data, isLoading } = useNutritionCustomerSearch(query)
  const brand = useNutritionBrand()
  const customers = data ?? []
  const text = query.trim()

  const go = (to: string, state?: NewCustomerState) => {
    onNavigate?.()
    navigate(to, { state })
  }

  return (
    <SidebarShell onToggle={onToggle} versionLabel={brand?.versionLabel ?? ''}>
      <Box sx={{ flexShrink: 0, px: 1.5, pt: 1 }}>
        <InputBase
          placeholder="お客様を探す"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'お客様を探す' }}
          startAdornment={
            <InputAdornment
              position="start"
              sx={{ mr: `${ICON_TEXT_GAP_PX}px` }}
            >
              <SearchIcon
                sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.muted }}
              />
            </InputAdornment>
          }
          sx={FILTER_ROW_SX}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 1.5,
          pb: 1,
        }}
      >
        <Row
          active={pathname === '/nutrition/new'}
          disabled={isActiveRecording}
          onOpen={() => go('/nutrition/new', { text })}
        >
          <AddIcon
            sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.muted }}
          />
          {/* 字が長ければ折り返す (切ると、どの字で登録するかが見えない) */}
          <RowLabel muted wrap>
            {text ? `「${text}」で新しいお客様を登録` : '新しいお客様を登録'}
          </RowLabel>
        </Row>
        {!isLoading &&
          customers.map((customer) => {
            const active = customer.id === openCustomerId
            return (
              <Row
                key={customer.id}
                active={active}
                disabled={isActiveRecording && !active}
                onOpen={() => go(`/nutrition/customers/${customer.id}`)}
                actions={<CustomerActions customer={customer} />}
              >
                <RowLabel bold={active}>{customer.name}</RowLabel>
                {customer.last_started_on && (
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontSize: scaledFontSize('tag'),
                      color: colorTokens.fg.faint,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatMd(customer.last_started_on)}
                  </Typography>
                )}
              </Row>
            )
          })}
        {!isLoading && text && customers.length === 0 && (
          <Typography
            sx={{
              px: 1,
              py: 1,
              fontSize: scaledFontSize('tag'),
              color: colorTokens.fg.faint,
            }}
          >
            見つかりません
          </Typography>
        )}
      </Box>
    </SidebarShell>
  )
}

// ピル状 row (design-book 02-components/sidebar.md)。押すと面を移る。actions は行末の hover-actions
// (乗ったとき・focus があるときに出る。面を移らないので、行が塞がれていても押せる)。
const Row = ({
  active,
  disabled,
  onOpen,
  actions,
  children,
}: {
  active: boolean
  disabled: boolean
  onOpen: () => void
  actions?: React.ReactNode
  children: React.ReactNode
}) => (
  <Tooltip
    title={disabled ? RECORDING_NAV_BLOCK_HINT : ''}
    placement="right"
    disableHoverListener={!disabled}
  >
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: LAYOUT.sidebarRowHeight,
        borderRadius: `${RADIUS.md}px`,
        backgroundColor: active ? colorTokens.bg.muted : undefined,
        transition: 'background-color 120ms',
        '&:hover':
          disabled || active
            ? undefined
            : { backgroundColor: colorTokens.bg.emphasis },
        '& .nutrition-row-actions': { opacity: 0, transition: 'opacity 120ms' },
        '&:hover .nutrition-row-actions, &:focus-within .nutrition-row-actions':
          { opacity: 1 },
      }}
    >
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          if (!disabled) onOpen()
        }}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          borderRadius: `${RADIUS.md}px`,
          '&:focus': { outline: 'none' },
          '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
        }}
      >
        {children}
      </Box>
      {actions && (
        <Box className="nutrition-row-actions" sx={{ flexShrink: 0, pr: 0.5 }}>
          {actions}
        </Box>
      )}
    </Box>
  </Tooltip>
)

const RowLabel = ({
  bold,
  muted,
  wrap,
  children,
}: {
  bold?: boolean
  muted?: boolean
  wrap?: boolean
  children: React.ReactNode
}) => (
  <Typography
    sx={{
      flex: 1,
      minWidth: 0,
      fontSize: scaledFontSize(muted ? 'tag' : 'label'),
      fontWeight: bold ? FONT_WEIGHT.medium : FONT_WEIGHT.regular,
      color: muted ? colorTokens.fg.muted : colorTokens.fg.default,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: wrap ? 'normal' : 'nowrap',
    }}
  >
    {children}
  </Typography>
)

// 行の ⋯ の中身は「お客様の情報」1 つ (4 欄と患者 ID の dialog。欄を離れた時点で保存)。
const CustomerActions = ({ customer }: { customer: NutritionCustomer }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  return (
    <>
      <IconButton
        size="small"
        aria-label={`${customer.name} のその他`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          width: 22,
          height: 22,
          borderRadius: `${RADIUS.sm}px`,
          color: colorTokens.fg.muted,
          '&:hover': { backgroundColor: colorTokens.bg.default },
        }}
      >
        <MoreHorizIcon sx={{ fontSize: ICON_SIZE.md }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: NOTION_MENU_PAPER_SX } }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null)
            setOpen(true)
          }}
        >
          お客様の情報
        </MenuItem>
      </Menu>
      <NutritionCustomerDialog
        open={open}
        customer={customer}
        onClose={() => setOpen(false)}
      />
    </>
  )
}
